/* eslint-disable camelcase */
/**
 * The FeatureFlags type is kept in a separate file because all teams need to
 * be able to edit their flags.
 *
 * @AppExplorer https://miro.com/app/board/uXjVPXskloA=/?moveToWidget=3458764537207536962&cot=14
 */

export type FeatureFlagWithExperiment = {
  status: boolean;
  inExperiment: boolean;
  values: any;
} & Partial<{
  variationId?: number;
  key?: string;
}>;

export const REGISTERED_BOOLEAN_FEATURE_FLAG_NAMES = [
  // This is a fake flag used as an example in Storybook
  'DocumentationFeatureFlag',
  'HelloSign_Prep_Send_Early_Next',
  'HelloSign_Request_Additional_Attachments',

  /* BEGIN Stars feature flags */
  'block_bulk_spam_risk',
  'sign_core_2024_06_12_new_editor_page',
  'sign_core_20240330_new_signer_app',
  'sign_core_2026_07_29_signer_app_legacy_fallback',
  /* END Stars feature flags */

  'HelloFax_Condors_20211215_Payment_Page',
  'HelloSign_Yetis_show_onboarding_tours',
  'HelloSign_Yetis_20210930_Hide_Billing_Period_For_Yearly',
  'HelloSign_Yetis_20220805_Onboarding_Education_Nav',

  'HelloSign_Bees_20220324_Email_Deliverability_Validation',
  'HelloSign_Bees_20220426_Gdrive_Modal',
  'HelloSign_Bees_20220831_cancel_subscription_discount',
  'HelloSign_Yetis_Dashboard_Redesign_Experiment',
  'HelloSign_Bees_20230412_Use_Updated_Lib_Phone_Number_Library',

  /* BEGIN Sign Services feature flags */
  'HelloSign_Yetis_20220824_is_first_signature_request_modal',
  'is_fingerprint_enabled',
  'sign_services_2026_01_20_field_auto_placement',
  'sign_services_2026_07_03_field_auto_placement_for_template',
  'sign_services_2026_05_15_field_auto_placement_beta_improvements',
  'sign_services_2026_07_09_template_usage_report',
  'sign_services_2026_10_06_domain_scoped_2fa_cookie',
  /* END Sign Services feature flags */

  /* BEGIN Sign Core feature flags */
  'sign_core_2025_07_30_prep_and_send_get_oas',
  'sign_core_20240322_lazy_load_fields',
  'sign_core_2024_10_23_vis2023',
  'Sign_Services_20240207_Fax_Sign_Bridge',
  'sign_core_2025_03_26_timestamp_signatures',
  'sign_core_2025_11_20_text_field_scaling',
  'sign_core_2025_11_24_email_reminder_notifications',
  'sign_core_2025_12_08_white_labeling_swap',
  'sign_core_2026_01_07_advanced_sig_frame',
  'sign_core_2026_01_19_new_settings',
  'sign_core_2026_01_19_auto_cleanup_failed_uploads',
  'sign_core_2026_02_19_fix_conditional_logic_original_required',
  'sign_core_2026_03_10_encode_api_ids_in_signer_app',
  'sign_core_2026_04_02_white_labeling_colors_import',
  'sign_systems_2026_05_04_file_upload_limit',
  'sign_core_2026_06_15_google_login_csrf_origin_validation',
  'sign_core_2026_06_17_fix_horizontal_scrollbar_on_embedded_iframe',
  'sign_core_2026_07_03_form_view_ui_improvements',
  'sign_core_2026_06_30_async_reorder_snapshots_frontend_fix',
  'sign_core_2026_07_07_fix_next_after_insert_everywhere',
  'sign_core_2026_07_22_document_summary_accessible_counts',
  'sign_core_2026_08_04_required_merge_fields',
  'sign_core_2026_08_17_improve_prep_and_send_onboarding_tours',
  'sign_core_2026_08_14_disable_first_fax_modal',
  'sign_core_2026_08_19_preparer_text_field_resizing',
  'sign_core_2026_09_04_keep_empty_preparer_field',
  'sign_core_2026_09_07_cl_fix_with_hidden_fields_reported',
  'sign_core_2026_08_24_multi_qes',
  'sign_core_2026_08_27_manage_contacts_in_settings',
  'sign_core_2026_09_11_resize_signer_attachments',
  'sign_core_2026_09_18_prep_and_send_initialization_routing',
  'sign_core_2026_10_02_freeze_sender_date_format',
  /* END Sign Core feature flags */

  /* BEGIN API Flags */
  'sign_api_2024_08_02_enforce_secure_callback_url_flag',
  'sign_api_2025_06_11_api_dashboard_detail_redesign_api_callback',
  /* END API Flags */

  /* BEGIN Systems feature flags */
  'sign_systems_2026_01_06_bulk_send_trial_rollout',
  'sign_systems_2026_04_08_validate_signature_canvas_size_client_side',
  /* END Systems feature flags */
] as const;

/** Flags populated via Growthbook::getExperimentInfo(); cookie overrides do not apply. */
export const EXPERIMENT_FEATURE_FLAG_NAMES = [
  'sign_services_2026_07_17_field_auto_placement_for_trial_users',
  'sign_core_2026_01_19_restrict_cancel_now_button_to_california_users',
] as const;

type BooleanFeatureFlagName =
  (typeof REGISTERED_BOOLEAN_FEATURE_FLAG_NAMES)[number];
type ExperimentFeatureFlagName = (typeof EXPERIMENT_FEATURE_FLAG_NAMES)[number];

export type FeatureFlags = Partial<
  Record<BooleanFeatureFlagName, boolean> &
    Record<ExperimentFeatureFlagName, boolean | FeatureFlagWithExperiment>
>;

/** All feature flags registered for the frontend. Used by dev tooling. */
export const REGISTERED_FEATURE_FLAG_NAMES = [
  ...REGISTERED_BOOLEAN_FEATURE_FLAG_NAMES,
  ...EXPERIMENT_FEATURE_FLAG_NAMES,
] as const;

const experimentFeatureFlagNameSet = new Set<string>(
  EXPERIMENT_FEATURE_FLAG_NAMES.map((name) => name.toLowerCase()),
);

export function isExperimentFeatureFlagName(flagName: string): boolean {
  return experimentFeatureFlagNameSet.has(flagName.trim().toLowerCase());
}
